import { motion, useScroll, useTransform } from 'framer-motion'
import { Button } from '../components/ui/button'
import { Link } from 'react-router-dom'
import { 
  Shield, 
  Globe, 
  Award, 
  TrendingUp, 
  ChevronRight, 
  Star, 
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Truck,
  Clock,
  Users,
  Leaf,
  Wheat,
  Coffee,
  Droplets,
  Gem,
  Zap,
  Building2,
  FileCheck2
} from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import CertificateCard from '../components/ui/CertificateCard'

const Home = () => {
  const { t } = useTranslation()
  const heroRef = useRef(null)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  })
  
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3])
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1])

  const products = [
    { 
      name: t('products.cattleFeed'),
      nameKey: 'Nutritious Cattle Feed',
      description: 'Balanced nutrition for optimal livestock health',
      image: '/cattle-feed/nutrious.png',
      path: '/products/cattle-feed',
      items: ['Protein Pellets', 'Corn Silage', 'Poultry Feed'],
      icon: Leaf
    },
    { 
      name: t('products.indianRice'),
      nameKey: 'Premium Indian Rice',
      description: 'Exquisite Basmati & Non-Basmati varieties, aged to perfection',
      image: '/rices/rice-1.png',
      path: '/products/rice',
      items: ['1121 Basmati', 'Golden Sella', 'Steam Basmati', 'IR-64'],
      icon: Wheat
    },
    { 
      name: t('products.pulses'),
      nameKey: 'Premium Pulses',
      description: 'High-protein legumes, carefully sorted and processed',
      image: '/pulse/pulse.png',
      path: '/products/pulses',
      items: ['Chickpeas', 'Kidney Beans', 'Lentils', 'Moong Beans'],
      icon: Coffee
    },
    { 
      name: t('products.spices'),
      nameKey: 'Authentic Spices',
      description: 'Pure, aromatic spices from India\'s finest regions',
      path: '/products/spices',
      image: '/spices/spice-1.png',
      items: ['Cardamom', 'Cumin', 'Turmeric', 'Black Pepper'],
      icon: Droplets
    },
    {
      name: t('products.cereals'),
      nameKey: 'Indian Cereals',
      description: 'Nutritious and wholesome cereals from India',
      path: '/products/cereals',
      image: '/cerals/cereal-1.png',
      items: ['Wheat', 'Rice', 'Barley', 'Maize'],
      icon: Wheat
    },
  ]

  const features = [
    {
      icon: Truck,
      title: t('home.globalLogistics'),
      description: t('home.globalLogisticsDesc'),
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Clock,
      title: t('home.support247'),
      description: t('home.support247Desc'),
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Users,
      title: t('home.directSourcing'),
      description: t('home.directSourcingDesc'),
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Gem,
      title: t('home.premiumQuality'),
      description: t('home.premiumQualityDesc'),
      color: 'from-green-500 to-emerald-500'
    }
  ]

  const certifications = [
    {
      title: t('home.importExportLicense'),
      description: t('home.importExportDesc'),
      icon: Globe,
    },
    {
      title: t('home.incorporationCert'),
      description: t('home.incorporationDesc'),
      icon: Building2,
    },
    {
      title: t('home.gstRegistration'),
      description: t('home.gstDesc'),
      icon: FileCheck2,
    },
    {
      title: t('home.fssaiLicense'),
      description : t('home.fssaiDesc'),
      image: '/certificates/fssai.png',
    },
    {
      title:t('home.apeda'),
      description:t('home.apedaDesc'),
      image: '/certificates/apeda.png',
    }
  ]
  return (
    <div className="overflow-x-hidden">
      {/* Hero Section with Parallax */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background with overlay */}
        <div className="absolute inset-0">
          <motion.div 
            style={{ opacity: heroOpacity, scale: heroScale }}
            className="absolute inset-0"
          >
            <img 
              src="/logo.jpeg" 
              alt="" 
              className="absolute inset-0 w-full h-full object-cover" 
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-slate-800/75 to-slate-900/90" />
            <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-10" />
          </motion.div>
        </div>

        {/* Animated particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              animate={{
                x: [Math.random() * window.innerWidth, Math.random() * window.innerWidth],
                y: [Math.random() * window.innerHeight, Math.random() * window.innerHeight],
                scale: [0, 1, 0],
                opacity: [0, 0.5, 0]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-32 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-8"
            >
              <Sparkles className="h-4 w-4 text-yellow-400" />
              <span className="text-white/90 text-sm font-medium">{t('home.welcomeBadge')}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-5xl md:text-7xl font-bold mb-6 [text-shadow:_0_2px_12px_rgba(0,0,0,0.5)]"
            >
              <span className="text-white">{t('home.heroTitle1')}</span>
              <span className="relative">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                  {t('home.heroTitle2')}
                </span>
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-3 bg-yellow-400/20 blur-xl"
                  animate={{ scaleX: [0.8, 1.2, 0.8] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </span>
              <br />
              <span className="text-white">{t('home.heroTitle3')}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-white/80 [text-shadow:_0_1px_8px_rgba(0,0,0,0.4)]"
            >
              {t('home.heroDescription')}
            </motion.p>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center">
            <div className="w-1 h-2 bg-white/60 rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* Products Preview with Modern Cards */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-600 mb-6"
            >
              <Zap className="h-4 w-4" />
              <span className="text-sm font-semibold">{t('home.premiumCollection')}</span>
            </motion.div>
            
            <h2 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                {t('home.featuredProducts')}
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              {t('home.featuredProductsDesc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product, index) => {
              const Icon = product.icon
              return (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -15 }}
                  className="group relative h-full"
                >
                  <Link to={product.path} className="block h-full">
                    {/* Card with gradient border */}
                    <div className="relative h-full bg-white rounded-3xl shadow-2xl overflow-hidden 
                                  before:absolute before:inset-0 before:p-[2px] before:rounded-3xl 
                                  before:bg-gradient-to-br before:from-primary-500 before:via-secondary-500 before:to-primary-500 
                                  before:opacity-0 before:transition-opacity before:duration-500 
                                  hover:before:opacity-100">
                      
                      <div className="relative h-full bg-white rounded-3xl overflow-hidden">
                        {/* Image container */}
                        <div className="relative h-56 overflow-hidden">
                          <motion.img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.7 }}
                          />
                          
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                          
                          {/* Icon */}
                          <div className="absolute top-4 right-4">
                            <div className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-md 
                                          flex items-center justify-center shadow-lg">
                              <Icon className="h-5 w-5 text-primary-600" />
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                          <h3 className="text-xl font-bold mb-2 group-hover:text-primary-600 transition-colors">
                            {product.name}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4">
                            {product.description}
                          </p>
                          
                          {/* Product items */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {product.items.slice(0, 3).map((item, i) => (
                              <span key={i} className="text-xs px-2 py-1 bg-primary-50 text-primary-600 rounded-full">
                                {item}
                              </span>
                            ))}
                          </div>

                          {/* View details */}
                          <div className="flex items-center text-primary-600 font-semibold text-sm group-hover:gap-3 gap-2 transition-all">
                            {t('home.viewDetails')}
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* About Section with Split Design */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-semibold mb-6">
                {t('home.aboutUs')}
              </span>
              <h2 className="text-4xl font-bold mb-6">
                {t('home.aboutTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('home.aboutDesc')}
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  t('home.aboutPoint1'),
                  t('home.aboutPoint2'),
                  t('home.aboutPoint3'),
                  t('home.aboutPoint4')
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>

              <Button 
                size="lg" 
                className="group bg-primary-600 hover:bg-primary-700"
                asChild
              >
                <Link to="/about">
                  {t('home.learnMore')}
                  <ArrowRight className="ml-2 rtl:mr-2 rtl:ml-0 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/about-image.jpg"
                  alt="KSD Ventures"
                  loading="lazy"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 to-transparent" />
              </div>
              
              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-6 max-w-xs"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <Award className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">10+ {t('about.yearsExcellence')}</div>
                    <div className="text-sm text-gray-600">{t('home.yearsExcellence')}</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      

      {/* Certifications Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-600 mb-6"
            >
              <Shield className="h-4 w-4" />
              <span className="text-sm font-semibold">{t('home.verifiedTrusted')}</span>
            </motion.div>

            <h2 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                {t('home.ourCertifications')}
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              {t('home.certificationsDesc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <CertificateCard
                key={cert.title}
                icon={cert.icon}
                image={cert.image}
                title={cert.title}
                description={cert.description}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Partners Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-600 mb-6"
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm font-semibold">{t('home.globalNetwork')}</span>
            </motion.div>
            <h2 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                {t('home.trustedPartners')}
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              {t('home.trustedPartnersDesc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -8 }}
              className="group bg-white rounded-3xl shadow-xl overflow-hidden w-full border border-gray-100 hover:shadow-2xl transition-all duration-300"
            >
              <div className="relative h-48 bg-gradient-to-br from-green-50 to-yellow-50 flex items-center justify-center p-8">
                <img
                  src="/partners/linbram-pvt-ltd.jpeg"
                  alt="Linbram Pvt Ltd"
                  loading="lazy"
                  className="max-h-full max-w-full object-contain"
                />
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                    {t('home.partnerSouthAfrica')}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Linbram Pvt Ltd</h3>
                <p className="text-gray-600 text-sm mb-4">{t('home.linbramDesc')}</p>
                <div className="flex items-center gap-2 text-sm text-primary-600 font-medium">
                  <Globe className="h-4 w-4" />
                  <span>{t('home.partnerLocation')}: {t('home.southAfrica')}</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ y: -8 }}
              className="group bg-white rounded-3xl shadow-xl overflow-hidden w-full border border-gray-100 hover:shadow-2xl transition-all duration-300"
            >
              <div className="relative h-48 bg-gradient-to-br from-green-50 to-yellow-50 flex items-center justify-center p-8">
                <img
                  src="/partners/bees-vegetables-uae.jpeg"
                  alt="Bees Vegetables & Fruits Trading LLC"
                  loading="lazy"
                  className="max-h-full max-w-full object-contain"
                />
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                    {t('home.partnerUAE')}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Bees Vegetables & Fruits Trading LLC</h3>
                <p className="text-gray-600 text-sm mb-4">{t('home.beesDesc')}</p>
                <div className="flex items-center gap-2 text-sm text-primary-600 font-medium">
                  <Globe className="h-4 w-4" />
                  <span>{t('home.partnerLocation')}: {t('home.uae')}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

       {/* Features Section */}
      <section className="py-20 bg-primary-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-5" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('home.whyChoose')}
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {t('home.whyChooseDesc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group"
              >
                <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 
                              hover:bg-white/10 transition-all duration-300 h-full">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} 
                                p-3 mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-white/70">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Parallax */}
      <section className="relative py-32 overflow-hidden">
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              'linear-gradient(45deg, #059669, #047857, #065f46)',
              'linear-gradient(45deg, #047857, #065f46, #059669)',
              'linear-gradient(45deg, #065f46, #059669, #047857)',
            ]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        
        <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-10" />
        
        <div className="relative max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="h-12 w-12 text-yellow-300 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              {t('home.ctaDesc')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-2xl"
                asChild
              >
                <Link to="/get-quote">
                  {t('home.requestQuote')}
                  <ArrowRight className="ml-2 rtl:mr-2 rtl:ml-0 h-5 w-5" />
                </Link>
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/40 text-primary-600 hover:bg-white/10 hover:border-white/60 px-8 py-6 text-lg backdrop-blur-sm"
                asChild
              >
                <Link to="/contact">
                  {t('home.contactSales')}
                </Link>
              </Button>
            </div>

        
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home